const ICONS = {
    pan: '<svg width="18" height="18" viewBox="0 0 18 18"><path fill="currentColor" d="M9 0.5 L12 3.5 H10 V8 H14.5 V6 L17.5 9 L14.5 12 V10 H10 V14.5 H12 L9 17.5 L6 14.5 H8 V10 H3.5 V12 L0.5 9 L3.5 6 V8 H8 V3.5 H6 Z"/></svg>',
    polygon: '<svg width="18" height="18" viewBox="0 0 18 18"><polygon fill="none" stroke="currentColor" stroke-width="2" points="9,1.5 16.5,7 13.5,16 4.5,16 1.5,7"/></svg>',
    rectangle: '<svg width="18" height="18" viewBox="0 0 18 18"><rect x="2" y="4" width="14" height="10" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
};

const MODE_TITLES = {
    pan: 'Pan the map',
    polygon: 'Draw a polygon — click to add points; click the first point, double-click or press Enter to finish; Esc cancels',
    rectangle: 'Draw a rectangle — click two opposite corners; Esc cancels',
};

export default class ShapeDrawer {

    constructor(gmaps, options = {}) {
        this.gmaps = gmaps;
        this.polygonOptions = options.polygonOptions || {};
        this.rectangleOptions = options.rectangleOptions || {};

        this.map = null;
        this.mode = null;               // 'pan' | 'polygon' | 'rectangle'
        this.handlers = {};             // eventName => [callbacks]

        this.toolbar = null;
        this.toolbarButtons = {};
        this.modeListeners = [];        // google.maps listeners active for the current mode

        // polygon drawing state
        this.path = [];                 // clicked vertices (LatLng)
        this.previewPolygon = null;
        this.firstVertexMarker = null;

        // rectangle drawing state
        this.anchor = null;             // first corner (LatLng)
        this.previewRectangle = null;

        this.onKeyDown = this.onKeyDown.bind(this);
    }

    addListener(eventName, callback) {
        (this.handlers[eventName] = this.handlers[eventName] || []).push(callback);
    }

    emit(eventName, payload) {
        (this.handlers[eventName] || []).forEach(cb => cb(payload));
    }

    setMap(map) {
        if (map === this.map) {
            return;
        }

        if (this.map) {
            this.cancelDrawing();
            this.exitMode();
            this.removeToolbar();
            document.removeEventListener('keydown', this.onKeyDown);
        }

        this.map = map;

        if (this.map) {
            this.createToolbar();
            document.addEventListener('keydown', this.onKeyDown);
            this.setMode('polygon');
        }
    }

    setMode(mode) {
        if (!this.map || mode === this.mode) {
            return;
        }

        this.cancelDrawing();
        this.exitMode();
        this.mode = mode;

        const drawing = mode === 'polygon' || mode === 'rectangle';
        this.map.setOptions({
            draggableCursor: drawing ? 'crosshair' : null,
            // Also stops an accidental zoom when the rectangle's second corner is double-clicked.
            disableDoubleClickZoom: drawing,
        });

        if (mode === 'polygon') {
            this.listen('click', e => this.polygonAddVertex(e.latLng));
            this.listen('mousemove', e => this.polygonPreview(e.latLng));
            this.listen('dblclick', () => this.polygonComplete());
        }

        if (mode === 'rectangle') {
            this.listen('click', e => this.rectangleCorner(e.latLng));
            this.listen('mousemove', e => this.rectanglePreview(e.latLng));
        }

        Object.keys(this.toolbarButtons).forEach((name) => {
            this.toolbarButtons[name].dataset.active = name === mode ? '1' : '';
            this.styleButton(this.toolbarButtons[name]);
        });
    }

    // ---- polygon -----------------------------------------------------------

    polygonAddVertex(latLng) {
        this.path.push(latLng);

        if (this.path.length === 1) {
            this.firstVertexMarker = new this.gmaps.Marker({
                position: latLng,
                map: this.map,
                zIndex: (this.polygonOptions.zIndex || 0) + 1,
                cursor: 'pointer',
                title: 'Click to finish the polygon',
                icon: {
                    path: this.gmaps.SymbolPath.CIRCLE,
                    scale: 5,
                    fillColor: '#ffffff',
                    fillOpacity: 1,
                    strokeColor: this.polygonOptions.fillColor || '#0000ff',
                    strokeWeight: 2,
                },
            });
            this.firstVertexMarker.addListener('click', () => this.polygonComplete());
        }
    }

    polygonPreview(latLng) {
        if (this.path.length === 0) {
            return;
        }

        if (!this.previewPolygon) {
            this.previewPolygon = new this.gmaps.Polygon({
                ...this.polygonOptions,
                clickable: false,
                map: this.map,
            });
        }

        this.previewPolygon.setPath([...this.path, latLng]);
    }

    polygonComplete() {
        // A double-click also fires a click that adds a vertex at the same spot —
        // drop consecutive duplicates before validating.
        const path = this.path.filter((point, i) => i === 0 || !point.equals(this.path[i - 1]));

        if (path.length < 3) {
            return;
        }

        this.cancelDrawing();

        const polygon = new this.gmaps.Polygon({
            ...this.polygonOptions,
            path,
            map: this.map,
        });
        this.emit('polygoncomplete', polygon);
    }

    // ---- rectangle ---------------------------------------------------------

    rectangleCorner(latLng) {
        if (!this.anchor) {
            this.anchor = latLng;
            this.previewRectangle = new this.gmaps.Rectangle({
                ...this.rectangleOptions,
                clickable: false,
                bounds: this.boundsFrom(this.anchor, latLng),
                map: this.map,
            });
            return;
        }

        if (latLng.equals(this.anchor)) {
            return;
        }

        const bounds = this.boundsFrom(this.anchor, latLng);
        this.cancelDrawing();

        const rectangle = new this.gmaps.Rectangle({
            ...this.rectangleOptions,
            bounds,
            map: this.map,
        });
        this.emit('rectanglecomplete', rectangle);
    }

    rectanglePreview(latLng) {
        if (this.previewRectangle) {
            this.previewRectangle.setBounds(this.boundsFrom(this.anchor, latLng));
        }
    }

    boundsFrom(a, b) {
        const bounds = new this.gmaps.LatLngBounds();
        bounds.extend(a);
        bounds.extend(b);
        return bounds;
    }

    // ---- shared ------------------------------------------------------------

    onKeyDown(e) {
        const inProgress = this.path.length > 0 || this.anchor !== null;
        if (!inProgress) {
            return;
        }

        // Don't hijack Enter/Esc while the user is typing (e.g. the location search box).
        if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) {
            return;
        }

        if (e.key === 'Escape') {
            e.preventDefault();
            this.cancelDrawing();
        }

        if (e.key === 'Enter' && this.mode === 'polygon') {
            e.preventDefault();
            this.polygonComplete();
        }
    }

    cancelDrawing() {
        this.path = [];
        this.anchor = null;

        if (this.previewPolygon) {
            this.previewPolygon.setMap(null);
            this.previewPolygon = null;
        }
        if (this.firstVertexMarker) {
            this.firstVertexMarker.setMap(null);
            this.firstVertexMarker = null;
        }
        if (this.previewRectangle) {
            this.previewRectangle.setMap(null);
            this.previewRectangle = null;
        }
    }

    exitMode() {
        this.modeListeners.forEach(listener => listener.remove());
        this.modeListeners = [];
        this.mode = null;

        if (this.map) {
            this.map.setOptions({ draggableCursor: null, disableDoubleClickZoom: false });
        }
    }

    listen(eventName, callback) {
        this.modeListeners.push(this.gmaps.event.addListener(this.map, eventName, callback));
    }

    // ---- toolbar -----------------------------------------------------------

    createToolbar() {
        this.toolbar = document.createElement('div');
        this.toolbar.style.cssText =
            'margin: 10px; display: flex; background: #fff; border-radius: 2px;' +
            'box-shadow: 0 1px 4px rgba(0,0,0,0.3); overflow: hidden;';

        ['pan', 'polygon', 'rectangle'].forEach((mode) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.title = MODE_TITLES[mode];
            button.innerHTML = ICONS[mode];
            button.style.cssText =
                'width: 34px; height: 34px; border: none; background: none; cursor: pointer;' +
                'display: flex; align-items: center; justify-content: center; color: #666;';
            button.addEventListener('click', () => this.setMode(mode));
            button.addEventListener('mouseenter', () => { button.style.background = button.dataset.active ? '#e8f0fe' : '#f1f1f1'; });
            button.addEventListener('mouseleave', () => { this.styleButton(button); });

            this.toolbar.appendChild(button);
            this.toolbarButtons[mode] = button;
        });

        this.map.controls[this.gmaps.ControlPosition.TOP_CENTER].push(this.toolbar);
    }

    styleButton(button) {
        const active = Boolean(button.dataset.active);
        button.style.background = active ? '#e8f0fe' : 'none';
        button.style.color = active ? '#1a73e8' : '#666';
    }

    removeToolbar() {
        if (!this.toolbar) {
            return;
        }

        const controls = this.map.controls[this.gmaps.ControlPosition.TOP_CENTER];
        for (let i = 0; i < controls.getLength(); i++) {
            if (controls.getAt(i) === this.toolbar) {
                controls.removeAt(i);
                break;
            }
        }

        this.toolbar = null;
        this.toolbarButtons = {};
    }
}
